import {
    AlertTriangle,
    CalendarDays,
    ChevronDown,
    ChevronRight,
    Circle,
    Database,
    type LucideIcon,
    Megaphone,
    MessageSquare,
    Scale,
    Sparkles,
    User,
    Users,
} from 'lucide-react';
import { type ReactElement, useId, useState } from 'react';

import { type StreamRunState } from '@apps/web/components/phoebe_chat/streaming_types';
import { PhoebeAgentMark } from '@apps/web/components/phoebe_chat_view/brand_marks';
import {
    type ExecutionPlanChecklistItem,
    type ExecutionPlanChecklistStatus,
} from '@apps/web/components/phoebe_chat_view/tool_calls/infrastructure_tool_ui';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { STATUS_INTENT_TOKENS } from '@apps/web/components/ui/status_tokens';
import { cn } from '@apps/web/lib/utils';

export type ProjectedRunMilestoneReceipt = {
    readonly rowCount: number;
    readonly fieldCount?: number;
    // undefined means the tool did not report whether rows were omitted.
    // null means rows were omitted but the exact count was unavailable.
    readonly omittedRowCount?: number | null;
};

export type ProjectedRunMilestoneItem = ExecutionPlanChecklistItem & {
    readonly receipt?: ProjectedRunMilestoneReceipt | null;
    readonly durationLabel?: string | null;
};

type MilestoneVisualKind =
    | 'caregivers'
    | 'clients'
    | 'messages'
    | 'outreach'
    | 'planning'
    | 'ranking'
    | 'records'
    | 'schedule';

const MILESTONE_VISUAL_ICONS: Record<MilestoneVisualKind, LucideIcon> = {
    caregivers: Users,
    clients: User,
    messages: MessageSquare,
    outreach: Megaphone,
    planning: Sparkles,
    ranking: Scale,
    records: Database,
    schedule: CalendarDays,
};

const MILESTONE_VISUAL_LABELS: Record<MilestoneVisualKind, string> = {
    caregivers: 'caregivers',
    clients: 'clients',
    messages: 'messages',
    outreach: 'outreach',
    planning: 'plan',
    ranking: 'matches',
    records: 'records',
    schedule: 'shifts',
};

const MILESTONE_STATUS_LABELS: Record<ExecutionPlanChecklistStatus, string> = {
    completed: 'Done',
    in_progress: 'In progress',
    error: 'Needs review',
    pending: 'Waiting',
};

const MILESTONE_STATUS_STYLES: Record<
    ExecutionPlanChecklistStatus,
    {
        readonly icon: string;
        readonly label: string;
        readonly title: string;
    }
> = {
    completed: {
        icon: cn(
            STATUS_INTENT_TOKENS.positive.background,
            STATUS_INTENT_TOKENS.positive.text,
        ),
        label: STATUS_INTENT_TOKENS.neutral.text,
        title: 'text-foreground',
    },
    in_progress: {
        icon: 'bg-violet-600 text-white dark:bg-violet-500',
        label: 'text-primary',
        title: 'text-foreground font-medium',
    },
    error: {
        icon: cn(
            STATUS_INTENT_TOKENS.warning.border,
            STATUS_INTENT_TOKENS.warning.background,
            STATUS_INTENT_TOKENS.warning.text,
        ),
        label: STATUS_INTENT_TOKENS.warning.text,
        title: STATUS_INTENT_TOKENS.warning.text,
    },
    pending: {
        icon: cn(
            STATUS_INTENT_TOKENS.neutral.border,
            STATUS_INTENT_TOKENS.neutral.background,
            STATUS_INTENT_TOKENS.neutral.text,
        ),
        label: STATUS_INTENT_TOKENS.neutral.text,
        title: 'text-muted-foreground',
    },
};

export function ProjectedRunMilestones({
    items,
    runStatus,
    defaultExpanded = false,
    className,
}: {
    readonly items: ReadonlyArray<ProjectedRunMilestoneItem>;
    readonly runStatus: StreamRunState['status'];
    readonly defaultExpanded?: boolean;
    readonly className?: string;
}): ReactElement {
    const contentId = useId();
    const [expansion, setExpansion] = useState({
        runStatus,
        isExpanded: defaultExpanded,
    });
    const isExpanded =
        expansion.runStatus === runStatus ? expansion.isExpanded : false;
    const summary = getMilestoneSummary(items, runStatus);
    const headline = getMilestoneHeadline(items, runStatus);
    const headerLabel = isExpanded
        ? headline
        : getCollapsedMilestoneHeadline(items, runStatus);
    const viewState = isExpanded
        ? 'expanded'
        : runStatus === 'running'
          ? 'live'
          : runStatus === 'errored'
            ? 'error'
            : 'done';

    return (
        <Card
            data-testid="reasoning-milestones"
            data-view-state={viewState}
            className={cn(
                'border-border w-full max-w-[49.625rem] gap-0 overflow-hidden rounded-2xl py-0 shadow-none',
                className,
            )}
        >
            <CardHeader className="grid min-h-10 grid-cols-[minmax(0,1fr)_auto] grid-rows-1 items-center gap-[9px] px-3.5 py-2.5">
                <div className="flex min-w-0 items-center gap-[9px]">
                    <PhoebeAgentMark className="size-5" />
                    <div className="min-w-0">
                        <CardTitle
                            role="heading"
                            aria-level={3}
                            className={cn(
                                'text-foreground truncate text-[13px] leading-[19.5px] font-semibold',
                                runStatus === 'running' &&
                                    'phoebe-shimmer-text',
                            )}
                        >
                            {headerLabel}
                        </CardTitle>
                        <CardDescription
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            className="sr-only"
                        >
                            {summary}
                        </CardDescription>
                    </div>
                </div>
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={contentId}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} milestones`}
                    onClick={() =>
                        setExpansion({
                            runStatus,
                            isExpanded: !isExpanded,
                        })
                    }
                    className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring inline-flex size-7 cursor-pointer items-center justify-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                    {isExpanded ? (
                        <ChevronDown aria-hidden="true" className="size-4" />
                    ) : (
                        <ChevronRight aria-hidden="true" className="size-4" />
                    )}
                </button>
            </CardHeader>
            {isExpanded ? (
                <CardContent id={contentId} className="px-3.5 pt-0.5 pb-3">
                    <ol aria-label="Observed milestones">
                        {items.map((item, index) => (
                            <ReasoningMilestoneRow
                                key={item.id}
                                item={item}
                                hasNext={index < items.length - 1}
                            />
                        ))}
                    </ol>
                </CardContent>
            ) : null}
        </Card>
    );
}

function ReasoningMilestoneRow({
    item,
    hasNext,
}: {
    readonly item: ProjectedRunMilestoneItem;
    readonly hasNext: boolean;
}): ReactElement {
    const styles = MILESTONE_STATUS_STYLES[item.status];
    const statusLabel = MILESTONE_STATUS_LABELS[item.status];
    const visualKind = getMilestoneVisualKind(item.title);

    return (
        <li
            data-testid={`reasoning-milestone-${item.id}`}
            data-status={item.status}
            data-visual={visualKind}
            className="relative grid min-h-8 grid-cols-[1.375rem_minmax(0,1fr)_auto] items-start gap-x-2.5 py-[5px] text-xs leading-5"
        >
            {hasNext ? (
                <span
                    aria-hidden="true"
                    className="bg-border/70 absolute top-[27px] -bottom-[5px] left-[10.25px] w-[1.5px]"
                />
            ) : null}
            <div className="relative flex min-h-[1.375rem] justify-center">
                <span
                    aria-hidden="true"
                    className={cn(
                        'relative z-10 flex size-[1.375rem] items-center justify-center rounded-full',
                        styles.icon,
                    )}
                >
                    <MilestoneStatusIcon
                        status={item.status}
                        visualKind={visualKind}
                    />
                </span>
            </div>
            <div className="min-w-0">
                <div
                    className={cn(
                        'text-[13px] leading-5 break-words',
                        styles.title,
                    )}
                >
                    {item.title}
                </div>
                {item.subtitle ? (
                    <div className="border-border/60 bg-muted/40 text-muted-foreground mt-1 inline-flex max-w-full rounded-md border px-2 py-0.5 font-mono text-[11px] leading-4 font-semibold break-words">
                        {item.subtitle}
                    </div>
                ) : null}
                {item.receipt ? (
                    <MilestoneReceipt
                        receipt={item.receipt}
                        subject={MILESTONE_VISUAL_LABELS[visualKind]}
                    />
                ) : null}
            </div>
            <div className="flex min-w-0 shrink-0 flex-col items-end pt-px leading-4">
                <span className="sr-only">{statusLabel}</span>
                {item.durationLabel ? (
                    <span className="text-muted-foreground text-[11px] leading-[16.5px] font-normal tabular-nums">
                        {item.durationLabel}
                    </span>
                ) : item.status === 'error' || item.status === 'pending' ? (
                    <span
                        className={cn('text-[11px] font-medium', styles.label)}
                    >
                        {statusLabel}
                    </span>
                ) : null}
            </div>
        </li>
    );
}

function MilestoneStatusIcon({
    status,
    visualKind,
}: {
    readonly status: ExecutionPlanChecklistStatus;
    readonly visualKind: MilestoneVisualKind;
}): ReactElement {
    if (status === 'error') {
        return <AlertTriangle aria-hidden="true" className="size-3" />;
    }
    if (status === 'pending') {
        return <Circle aria-hidden="true" className="size-2.5" />;
    }
    const VisualIcon = MILESTONE_VISUAL_ICONS[visualKind];
    return <VisualIcon aria-hidden="true" className="size-3" />;
}

function MilestoneReceipt({
    receipt,
    subject,
}: {
    readonly receipt: ProjectedRunMilestoneReceipt;
    readonly subject: string;
}): ReactElement {
    const rowLabel = `${receipt.rowCount} ${receipt.rowCount === 1 ? 'row' : 'rows'}`;
    const fieldLabel =
        receipt.fieldCount === undefined
            ? null
            : `${receipt.fieldCount} ${receipt.fieldCount === 1 ? 'field' : 'fields'}`;
    const receiptAriaLabel = fieldLabel
        ? `Retrieved ${rowLabel} for ${subject} across ${fieldLabel}`
        : `Retrieved ${rowLabel} for ${subject}`;
    const hasPartialResult =
        receipt.omittedRowCount === null ||
        (receipt.omittedRowCount !== undefined && receipt.omittedRowCount > 0);
    const partialLabel =
        receipt.omittedRowCount === null
            ? 'more available'
            : `+${receipt.omittedRowCount ?? 0} more`;
    const partialAriaLabel =
        receipt.omittedRowCount === null
            ? 'Additional matching rows were not returned'
            : `${receipt.omittedRowCount ?? 0} additional matching ${receipt.omittedRowCount === 1 ? 'row was' : 'rows were'} not returned`;

    return (
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span
                aria-label={receiptAriaLabel}
                className="border-border/60 bg-muted/35 text-muted-foreground inline-flex min-h-[25px] max-w-full flex-wrap items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono text-[11px] leading-[17.25px] font-semibold"
            >
                <span>retrieve ·</span>
                <span className="text-violet-700 dark:text-violet-300">
                    {subject}
                </span>
                <span>{`· ${rowLabel}`}</span>
                {fieldLabel ? <span>{`· ${fieldLabel}`}</span> : null}
            </span>
            {hasPartialResult ? (
                <span
                    aria-label={partialAriaLabel}
                    className={cn(
                        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] leading-4 font-medium',
                        STATUS_INTENT_TOKENS.warning.border,
                        STATUS_INTENT_TOKENS.warning.background,
                        STATUS_INTENT_TOKENS.warning.text,
                    )}
                >
                    <AlertTriangle aria-hidden="true" className="size-3" />
                    {partialLabel}
                </span>
            ) : null}
        </div>
    );
}

function getMilestoneVisualKind(title: string): MilestoneVisualKind {
    const normalizedTitle = title.toLocaleLowerCase();
    if (/caregiver|employee|staff|worker/u.test(normalizedTitle)) {
        return 'caregivers';
    }
    if (/client|patient/u.test(normalizedTitle)) {
        return 'clients';
    }
    if (/outreach|callout|campaign/u.test(normalizedTitle)) {
        return 'outreach';
    }
    if (/message|text|email|sms/u.test(normalizedTitle)) {
        return 'messages';
    }
    if (/rank|match|score|compare/u.test(normalizedTitle)) {
        return 'ranking';
    }
    if (/shift|schedule|visit|coverage|calendar/u.test(normalizedTitle)) {
        return 'schedule';
    }
    if (
        /plan|prepare|preparing|preparation|next step|finalize|finalizing|finalization/u.test(
            normalizedTitle,
        )
    ) {
        return 'planning';
    }
    return 'records';
}

function getMilestoneSummary(
    items: ReadonlyArray<ExecutionPlanChecklistItem>,
    runStatus: StreamRunState['status'],
): string {
    if (runStatus === 'errored') {
        return 'Needs review';
    }

    const completedCount = items.filter(
        (item) => item.status === 'completed',
    ).length;
    if (runStatus === 'completed') {
        return `${completedCount} of ${items.length} complete`;
    }

    const isWorking = items.some((item) => item.status === 'in_progress');
    if (isWorking && completedCount > 0) {
        return `${completedCount} completed · Working`;
    }
    if (isWorking) {
        return 'Working';
    }
    if (completedCount > 0) {
        return `${completedCount} completed`;
    }
    return 'Getting started';
}

function getMilestoneHeadline(
    items: ReadonlyArray<ExecutionPlanChecklistItem>,
    runStatus: StreamRunState['status'],
): string {
    if (runStatus === 'running') {
        const activeTitle = items
            .find((item) => item.status === 'in_progress')
            ?.title.trim();
        if (!activeTitle) {
            return 'Working…';
        }
        return `${activeTitle.replace(/(?:\.\.\.|…)+$/u, '').trimEnd()}…`;
    }

    return (
        items.find((item) => item.status === 'error')?.title.trim() ||
        items[items.length - 1]?.title.trim() ||
        'Milestones'
    );
}

function getCollapsedMilestoneHeadline(
    items: ReadonlyArray<ExecutionPlanChecklistItem>,
    runStatus: StreamRunState['status'],
): string {
    if (runStatus === 'running') {
        return getMilestoneHeadline(items, runStatus);
    }
    if (runStatus === 'errored') {
        return 'Reasoning needs review';
    }
    const completedCount = items.filter(
        (item) => item.status === 'completed',
    ).length;
    return `${completedCount} ${completedCount === 1 ? 'step' : 'steps'} complete`;
}
